import { isNil } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configDatabaseRecordType,
  configPageableMetaType,
  configSystemRecordType,
  IDatabaseRecord,
  IPageable,
  IPartialPageable,
  ISystemRecord,
} from "@/core/api";
import { dateTimeType, TDateTime } from "@/core/dates";
import { attachmentType, IAttachment } from "@/data/attachments";
import { coordinatesType, ICoordinates } from "@/data/locations";

export interface ILegalEntityShort {
  id: number;
  backgroundImageSystemId: string | null;
  backgroundImagePublicUrl: string | null;
  logoSystemId: string | null;
  logoPublicUrl: string | null;
  name: string;
  publicProfileId: number | null;
  publicProfileSlug: string | null;
  publicProfileName: string | null;
  aboutUs?: string | null;
  aboutUsFlavorText?: string | null;
  vatNumber?: string | null;
  country?: string | null;
}

const configLegalEntityShortType: TType<ILegalEntityShort> = {
  id: numberType(NaN),
  backgroundImageSystemId: stringType(""),
  backgroundImagePublicUrl: stringType(""),
  logoSystemId: stringType(""),
  logoPublicUrl: stringType(""),
  name: stringType(""),
  publicProfileId: numberType(null),
  publicProfileSlug: stringType(null),
  publicProfileName: stringType(null),
  aboutUs: stringType(""),
  aboutUsFlavorText: stringType(""),
  vatNumber: stringType(""),
  country: stringType(""),
};

export const legalEntityShortType = ensure(configLegalEntityShortType);

export interface ILegalEntity extends IDatabaseRecord, ISystemRecord {
  name: string;
  country: string;
  city: string;
  zipCode: string;
  street: string;
  phoneNumber: string;
  headquarter: boolean;
  coordinates: ICoordinates;
}

const configLegalEntityType: TType<ILegalEntity> = {
  ...configDatabaseRecordType,
  ...configSystemRecordType,
  name: stringType(""),
  country: stringType(""),
  city: stringType(""),
  zipCode: stringType(""),
  street: stringType(""),
  phoneNumber: stringType(""),
  headquarter: booleanType(false),
  coordinates: v => (isNil(v) ? undefined : coordinatesType(v)),
};

const legalEntityType = ensure(configLegalEntityType);

export interface IPublicProfile {
  id: number;
  signature: string;
  name: string;
  slug: string;
  legalEntities: ILegalEntity[];
  facebookSlug: string | null;
  twitterSlug: string | null;
  instagramSlug: string | null;
  linkedinSlug: string | null;
  aboutUs: string;
  logo: IAttachment;
  backgroundImage: IAttachment;
  benefits: string[];
  companySize: string | null;
  phoneNumber: string | null;
  website: string | null;
  newsCount: number;
}

const configPublicProfileType: TType<IPublicProfile> = {
  id: numberType(NaN),
  signature: stringType(""),
  legalEntities: ensureArray(legalEntityType),
  name: stringType(""),
  slug: stringType(""),
  facebookSlug: stringType(null),
  twitterSlug: stringType(null),
  instagramSlug: stringType(null),
  linkedinSlug: stringType(null),
  aboutUs: stringType(""),
  logo: attachmentType,
  backgroundImage: attachmentType,
  benefits: ensureArray(stringType("")),
  companySize: stringType(null),
  phoneNumber: stringType(null),
  website: stringType(null),
  newsCount: numberType(0),
};

export const publicProfileType = ensure(configPublicProfileType);

export interface IPublicProfilesFollowedData extends IDatabaseRecord, ISystemRecord {
  followersCount: number;
  jobRequestCount: number;
  publicProfileId: number;
}

export interface IPublicProfileInfo {
  followersCount: number;
  jobRequestCount: number;
}

const configPublicProfileInfoType: TType<IPublicProfileInfo> = {
  followersCount: numberType(0),
  jobRequestCount: numberType(0),
};

export const publicProfileInfoType = ensure(configPublicProfileInfoType);

export interface IPublicProfileFollowStatus {
  following: boolean;
}

const configPublicProfileFollowStatus: TType<IPublicProfileFollowStatus> = {
  following: booleanType("false"),
};

export const publicProfileFollowStatusType = ensure(configPublicProfileFollowStatus);

export interface IPageablePublicProfile extends IPageable<IPublicProfile[]> {}

export interface IPublicProfileNews {
  id: number;
  signature: string;
  publicationDate: TDateTime;
  title: string;
  coverImage: IAttachment;
  longText: string;
  longFlavorText: string;
  shortText: string | null;
  publicProfile: IPublicProfile;
}

const configPublicProfileNewsType: TType<IPublicProfileNews> = {
  id: numberType(NaN),
  signature: stringType(""),
  publicationDate: dateTimeType(),
  title: stringType(""),
  coverImage: attachmentType,
  longText: stringType(""),
  longFlavorText: stringType(""),
  shortText: stringType(null),
  publicProfile: publicProfileType,
};

export const publicProfileNewsType = ensure(configPublicProfileNewsType);

export interface IPageablePublicProfileNews extends IPageable<IPublicProfileNews[]> {}

export const configPageablePublicProfileNewsType: TType<IPageablePublicProfileNews> = {
  ...configPageableMetaType,
  content: ensureArray(publicProfileNewsType),
};

export const pageablePublicProfileNewsType = ensure(configPageablePublicProfileNewsType);

export interface IPartialPageablePublicProfileNews extends IPartialPageable<IPublicProfileNews[]> {}
